import { Apartment, AutoFixHigh, TravelExplore } from '@mui/icons-material';
import { Button, Grid, Stack, TextField, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { stringify } from 'json5';
import { debounce, get, includes, isArray, isNil, pickBy, some } from 'lodash';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronsDown, ChevronsUp, Search } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import FormikForm from 'src/components/common/FormikForm';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import FormAutoCompleteByCountry from 'src/components/Form/FormAutoCompleteByCountry';
import FormItemCountry from 'src/components/Form/FormItemCountry';
import FormSearchHsCode from 'src/components/Form/FormSearchHsCode';
import FormSearchHsCodeByAI from 'src/components/Form/FormSearchHsCodeByAI';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import MenuItemAIHsCodeLabelingIcon from 'src/components/Icons/MenuItemAIHsCodeLabeling';
import { CompanyTradeDataTab } from 'src/features/CompanyTradeData';
import { TradeDataPublicSearchBy } from 'src/features/Public/TradeData/config';
import { TradeDataSearchBy } from 'src/features/TradeData/Common/FormSearchGlobalTrade';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryUsageInformation } from 'src/services/queries';
import { useQueryGetSentencePrediction } from 'src/services/queries/useQueryAILabeling';
import {
  useQueryCTDTransactionsCount,
  useQueryCTDTransactionsPaymentStatus,
} from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import {
  useQueryListTradeDataHSCode,
  useQueryTransactionsCount,
  useQueryTransactionsPaymentStatus,
} from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST_BILLS_OF_LADING } from 'src/utils/constants/config';
import { COUNTRIES } from 'src/utils/constants/countries';
import { TypeCompanyTradeDataSearch } from 'src/utils/constants/enum';
import { FeatureAction, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { sortObjectsArrayByKey } from 'src/utils/helpers/arrayHelpers';
import { getTradeDataEndMonth } from 'src/utils/helpers/dayjs';
import { formatNumberWithSuffix } from 'src/utils/helpers/number';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { getFormItemError, renderFormItemError } from 'src/utils/helpers/renderFormItemError';
import { getCountryByCountryCode, normalizeHSCodeToHS6 } from 'src/utils/helpers/stringHelpers';
import * as yup from 'yup';

const TradeDataMainSearch = () => {
  const [hsCodeSearchKeyword, setHSCodeSearchKeyword] = useState('');
  const [hsCodeAISearchKeyword, setHsCodeAISearchKeyword] = useState('');
  const [searchBy, setSearchBy] = useState<TradeDataSearchBy | TradeDataPublicSearchBy | TypeCompanyTradeDataSearch>(
    TradeDataSearchBy.HS_CODE_AI,
  );
  const [searchKeyword, setSearchKeyword] = useState('');
  const [visibleAdvanceSearch, setVisibleAdvanceSearch] = useState(false);

  const navigate = useNavigate();
  const {
    palette: { background, common, info },
    shape: { borderRadius },
    spacing,
  } = useTheme();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const { t } = useTranslation();
  const params = useQueryParams();

  const [initTradeFrom, initTradeTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, [runtimeConfig.DEFAULT_TRADE_DATA_FROM, runtimeConfig.DEFAULT_TRADE_DATA_TO]);

  const getDefaultValue = (key: string) => {
    return get(params, key, '') as string;
  };
  const { data: dataUsage } = useQueryUsageInformation();

  const featureName = useMemo(() => {
    switch (searchBy) {
      case TradeDataSearchBy.HS_CODE:
      case TradeDataSearchBy.HS_CODE_AI: {
        return LIST_FEATURES.TRADE_DATA_BUYER_COUNTRY;
      }
      case TypeCompanyTradeDataSearch.BOTH:
      case TradeDataPublicSearchBy.COMPANY_BUYER:
      case TradeDataPublicSearchBy.COMPANY_SUPPLIER: {
        return LIST_FEATURES.COMPANY_PROFILE_DATA_ANALYTICS;
      }
      default: {
        return LIST_FEATURES.TRADE_DATA_BUYER_COUNTRY;
      }
    }
  }, [searchBy]);

  const { getFeaturePoints, hasAccess } = useFeatureAccess();
  const isAccessBuyer = hasAccess([URL_ENDPOINT.tradeData.buyersTable]);
    const tradeDataEndpoint = isAccessBuyer ? URL_ENDPOINT.tradeData.buyersTable : URL_ENDPOINT.tradeData.suppliersTable;
    const pointsViewTradeData = getFeaturePoints(tradeDataEndpoint, FeatureAction.VIEW);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      hs_code: null as ITradeDataProductItem | null,
      hs_code_ai: null as TypeSingleHSCodePrediction | null,
      buyer_keyword: getDefaultValue('buyer_keyword'),
      supplier_keyword: getDefaultValue('supplier_keyword'),
      trade_from: initTradeFrom,
      trade_to: initTradeTo,
      keyword: '',
      import_country: getCountryByCountryCode(getDefaultValue('imp_country_code')),
      export_country: getCountryByCountryCode(getDefaultValue('exp_country_code')),
      country_code: getDefaultValue('country_code'),
    },
    validationSchema: yup.object().shape({
      trade_from: yup
        .mixed()
        .required('Trade Date From is required')
        .test('is-greater', t('trade_data.common.field.error.date_range.invalid'), function (_, context) {
          const trade_from = dayjs(context.parent.trade_from || 'Invalid date');
          const trade_to = dayjs(context.parent.trade_to || 'Invalid date');
          if (!(trade_from.isValid() && trade_to.isValid())) return true;
          return trade_from.isBefore(trade_to);
        }),
    }),
    onSubmit(values, { setSubmitting }) {
      switch (searchBy) {
        case TradeDataSearchBy.HS_CODE:
        case TradeDataSearchBy.HS_CODE_AI: {
          handleSearchHsCode();
          break;
        }
        case TypeCompanyTradeDataSearch.BOTH:
        case TradeDataPublicSearchBy.COMPANY_BUYER:
        case TradeDataPublicSearchBy.COMPANY_SUPPLIER: {
          handleSearchCompany();
          break;
        }
        default: {
          break;
        }
      }
      setSubmitting(false);
    },
  });

  const { values } = formik;
  const payloadHsCode = useMemo(
    () => ({
      hs_code:
        searchBy === TradeDataSearchBy.HS_CODE
          ? values.hs_code?.code
          : normalizeHSCodeToHS6(values.hs_code_ai?.hs_code),
      start_date: values.trade_from?.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
      buyer_keyword: values.buyer_keyword || undefined,
      supplier_keyword: values.supplier_keyword || undefined,
      end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
      offset: 0,
      limit: LIMIT_GET_LIST_BILLS_OF_LADING,
      _search_by: searchBy,
      exp_country_code: values.export_country?.code,
      imp_country_code: values.import_country?.code,
    }),
    [values, searchBy],
  );

  const payloadCompanyKeyword = useMemo(
    () =>
      pickBy(
        {
          start_date: values.trade_from?.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
          end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
          offset: 0,
          limit: LIMIT_GET_LIST_BILLS_OF_LADING,
          keyword: searchKeyword,
          is_buyer: searchBy === TradeDataPublicSearchBy.COMPANY_BUYER,
          country_code: values?.country_code,
          company_type: searchBy === TypeCompanyTradeDataSearch.BOTH ? 'both' : undefined,
        },
        (value) => !isNil(value) && value !== '',
      ),
    [searchKeyword, searchBy, values],
  );

  const countTransactionPayload = {
    [TradeDataSearchBy.HS_CODE]: payloadHsCode,
    [TradeDataSearchBy.HS_CODE_AI]: payloadHsCode,
    [TradeDataPublicSearchBy.COMPANY_BUYER]: payloadCompanyKeyword,
    [TradeDataPublicSearchBy.COMPANY_SUPPLIER]: payloadCompanyKeyword,
    [TypeCompanyTradeDataSearch.BOTH]: payloadCompanyKeyword,
  };

  const transactionsCTDPaymentStatusResp = useQueryCTDTransactionsPaymentStatus(
    formik.isValid && !formik.isValidating ? payloadCompanyKeyword : null,
  );

  const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(
    formik.isValid && !formik.isValidating ? payloadHsCode : null,
  );

  const isTransactionQueryPaid =
    searchBy === TradeDataSearchBy.HS_CODE || searchBy === TradeDataSearchBy.HS_CODE_AI
      ? transactionsPaymentStatusResp.data?.data?.payment_status
      : transactionsCTDPaymentStatusResp.data?.data?.payment_status;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchKeyword(values.keyword);
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [values.keyword]);

  const isHSCodeSearch = includes([TradeDataSearchBy.HS_CODE, TradeDataSearchBy.HS_CODE_AI], searchBy);

  const transactionsCountByHSCodeResp = useQueryTransactionsCount(
    isHSCodeSearch ? countTransactionPayload[searchBy] : undefined,
  );

  const transactionsCountByCompanyRespCompany = useQueryCTDTransactionsCount(
    !isHSCodeSearch ? countTransactionPayload[searchBy] : undefined,
  );

  const isEmptyTransaction = useMemo(() => {
    if (isHSCodeSearch) {
      return isNil(transactionsCountByHSCodeResp.data?.data);
    }
    return isNil(transactionsCountByCompanyRespCompany.data?.data);
  }, [stringify(transactionsCountByHSCodeResp), stringify(transactionsCountByCompanyRespCompany)]);

  const handleSearchHsCode = () => {
    const searchString =
      handleQueryParamFromObject(pickBy(payloadHsCode, (value) => !(value === undefined || value === null))) +
      `&tab=${TypeTabTradeData.BuyerCountry}`;
    navigate({ pathname: AuthRouteKeys.TradeData, search: searchString });
  };
  const handleSearchCompany = () => {
    // const tab = payloadCompanyKeyword.is_buyer ? TypeTabTradeData.Suppliers : TypeTabTradeData.Buyers;
    const tab = CompanyTradeDataTab.Analytics;

    const searchString = handleQueryParamFromObject(payloadCompanyKeyword) + `&tab=${tab}`;
    navigate({
      pathname: AuthRouteKeys.CompanyTradeData,
      search: searchString,
    });
  };

  const dataHSCodeAIOptionsResp = useQueryGetSentencePrediction({
    text: hsCodeAISearchKeyword,
  });
  const getError = (id: string) => {
    return get(formik.touched, id) && Boolean(get(formik.errors, id));
  };

  const dataHSCodeOptionsResp = useQueryListTradeDataHSCode({
    country_code: '',
    keyword: hsCodeSearchKeyword || '',
    limit: 50,
  });

  const filterSearchBy = useMemo(() => {
    return [
      {
        label: 'AI Search',
        Icon: AutoFixHigh,
        key: TradeDataSearchBy.HS_CODE_AI,
      },
      {
        label: 'HS Code',
        Icon: MenuItemAIHsCodeLabelingIcon,
        key: TradeDataSearchBy.HS_CODE,
      },
      {
        label: 'Buyer Name',
        Icon: Apartment,
        key: TradeDataPublicSearchBy.COMPANY_BUYER,
      },
      {
        label: 'Supplier Name',
        Icon: Apartment,
        key: TradeDataPublicSearchBy.COMPANY_SUPPLIER,
      },
      {
        label: 'Both Buyer & Supplier',
        Icon: Apartment,
        key: TypeCompanyTradeDataSearch.BOTH,
      },
    ].map(({ label, Icon, key }) => (
      <ButtonWithLoading
        onClick={() => {
          setSearchBy(key);
        }}
        color={searchBy === key ? 'primary' : 'inherit'}
        isRounded
        variant={searchBy === key ? 'contained' : 'outlined'}
        startIcon={<Icon sx={{ color: 'inherit', bgcolor: 'inherit', fill: 'currentcolor' }} />}
        size="small"
        key={key}
      >
        {label}
      </ButtonWithLoading>
    ));
  }, [searchBy]);

  const countTransactionHsCode = (
    <Fragment>
      {transactionsCountByHSCodeResp.isFetching && (
        <Typography align="center" variant="caption2" color="text.secondary">
          Please wait a few seconds while we compile your report.
        </Typography>
      )}

      {transactionsCountByHSCodeResp.data?.data !== undefined && !transactionsCountByHSCodeResp.isFetching ? (
        <Box
          width={'100%'}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
          }}
        >
          <Typography variant={'body2'}>
            We've found
            <Box mx={0.5} component={'span'} color={info.main}>
              {formatNumberWithSuffix(transactionsCountByHSCodeResp.data?.data)} Bills of Lading
            </Box>
            matching with
            <Box mx={0.5} component={'span'} color={info.main}>
              HSCode {payloadHsCode.hs_code}
            </Box>
          </Typography>
        </Box>
      ) : null}
    </Fragment>
  );
  const countTransactionCompany = (
    <Fragment>
      {transactionsCountByCompanyRespCompany.isFetching && (
        <Typography align="center" variant="caption2" color="text.secondary">
          Please wait a few seconds while we compile your report.
        </Typography>
      )}

      {transactionsCountByCompanyRespCompany.data?.data !== undefined &&
      !transactionsCountByCompanyRespCompany.isFetching ? (
        <Box
          width={'100%'}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
          }}
        >
          <Typography variant={'body2'}>
            We've found
            <Box mx={0.5} component={'span'} color={info.main}>
              {formatNumberWithSuffix(transactionsCountByCompanyRespCompany.data?.data)} Bills of Lading
            </Box>
            matching with keyword
            <Box mx={0.5} component={'span'} color={info.main}>
              {searchKeyword}
            </Box>
            as
            <Box mx={0.5} component={'span'} color={info.main}>
              {searchBy === TypeCompanyTradeDataSearch.BOTH
                ? 'Buyer or Supplier'
                : searchBy === TradeDataPublicSearchBy.COMPANY_BUYER
                  ? 'Buyer'
                  : 'Supplier'}
            </Box>
          </Typography>
        </Box>
      ) : null}
    </Fragment>
  );

  const buttonAdvanceSearch = (
    <Button
      onClick={() => setVisibleAdvanceSearch(!visibleAdvanceSearch)}
      endIcon={visibleAdvanceSearch ? <ChevronsUp size={16} /> : <ChevronsDown size={16} />}
      size="small"
      color="suggestion"
    >
      Advance Search
    </Button>
  );

  const advancedSearchByHsCodeForm = (
    <Fragment>
      {visibleAdvanceSearch && (
        <>
          <Box width={'100%'}>
            <Grid sx={{ padding: 0 }} container spacing={2}>
              <Grid item xs={6}>
                <FormAutoCompleteByCountry
                  id={'export_country'}
                  countryCodeField={'code'}
                  getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
                  labelField={'label'}
                  isError={
                    (formik.touched['export_country'] || formik.touched['import_country']) &&
                    Boolean(formik.errors['export_country'])
                  }
                  value={formik.values.export_country}
                  countryCode={formik.values.export_country?.code}
                  placeholder={t('trade_data.search_form.input.export_country')}
                  onBlur={() => {
                    formik.setFieldTouched('export_country');
                  }}
                  onChange={(value: ITradeDataPortInfo) => {
                    formik.setFieldValue('export_country', value);
                  }}
                  options={sortObjectsArrayByKey(COUNTRIES, 'label')}
                />
              </Grid>
              <Grid item xs={6}>
                <FormAutoCompleteByCountry
                  id={'import_country'}
                  getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
                  labelField={'label'}
                  isError={
                    (formik.touched['export_country'] || formik.touched['import_country']) &&
                    Boolean(formik.errors['export_country'])
                  }
                  countryCodeField={'code'}
                  value={formik.values.import_country}
                  countryCode={formik.values.import_country?.code}
                  placeholder={t('trade_data.search_form.input.import_country')}
                  onBlur={() => {
                    formik.setFieldTouched('import_country');
                  }}
                  onChange={(value: ITradeDataPortInfo) => {
                    formik.setFieldValue('import_country', value);
                  }}
                  options={sortObjectsArrayByKey(COUNTRIES, 'label')}
                />
              </Grid>
            </Grid>
          </Box>
          <Box width={'100%'}>
            <Grid sx={{ padding: 0 }} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  defaultValue={formik.values.supplier_keyword}
                  label="Keyword (Supplier Name)"
                  fullWidth
                  onChange={debounce(formik.handleChange, 500)}
                  onBlur={formik.handleBlur}
                  size="small"
                  id="keyword"
                  name="supplier_keyword"
                  placeholder="Keyword (Supplier Name)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  defaultValue={formik.values.buyer_keyword}
                  onChange={debounce(formik.handleChange, 500)}
                  label="Keyword (Buyer Name)"
                  fullWidth
                  onBlur={formik.handleBlur}
                  size="small"
                  id="keyword"
                  name="buyer_keyword"
                  placeholder="Keyword (Buyer Name)"
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      {buttonAdvanceSearch}
    </Fragment>
  );

  const advancedSearchByKeywordForm = (
    <Fragment>
      {visibleAdvanceSearch && (
        <Box width={'100%'}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormItemCountry label="Country" id="country_code" />
            </Grid>
          </Grid>
        </Box>
      )}
      {buttonAdvanceSearch}
    </Fragment>
  );

  const advancedSearchForm = {
    [TradeDataSearchBy.HS_CODE]: advancedSearchByHsCodeForm,
    [TradeDataSearchBy.HS_CODE_AI]: advancedSearchByHsCodeForm,
    [TradeDataPublicSearchBy.COMPANY_BUYER]: advancedSearchByKeywordForm,
    [TradeDataPublicSearchBy.COMPANY_SUPPLIER]: advancedSearchByKeywordForm,
    [TypeCompanyTradeDataSearch.BOTH]: advancedSearchByKeywordForm,
  };

  const searchForm = {
    [TradeDataSearchBy.HS_CODE]: (
      <Fragment>
        <FormSearchHsCode
          required={true}
          id={'hs_code'}
          onBlur={() => {
            formik.setFieldTouched('hs_code');
          }}
          inputValue={hsCodeSearchKeyword || ''}
          value={formik.values.hs_code}
          options={dataHSCodeOptionsResp.data?.data}
          onChange={(value: ExpectedAny) => {
            // setHSCodeValue(value)
            formik.setFieldValue('hs_code', value);
          }}
          onTextInputChange={(value: string) => {
            setHSCodeSearchKeyword(value);
          }}
          loading={dataHSCodeOptionsResp.isFetching}
          isError={getError('hs_code')}
        />
        <Box width={'100%'}>
          <Grid sx={{ padding: 0 }} container spacing={2}>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_from}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_from', data);
                  formik.setFieldTouched('trade_from');
                }}
                label={t('trade_data.search_form.input.trade_from')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                    helperText: renderFormItemError(getFormItemError('trade_from', formik)),
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_to}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_to', data);
                  formik.setFieldTouched('trade_to');
                }}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
          </Grid>
        </Box>
        {advancedSearchForm[searchBy]}
        {countTransactionHsCode}
      </Fragment>
    ),
    [TradeDataSearchBy.HS_CODE_AI]: (
      <Fragment>
        <FormSearchHsCodeByAI
          required={true}
          id={'hs_code_ai'}
          onBlur={() => {
            formik.setFieldTouched('hs_code_ai');
          }}
          inputValue={hsCodeSearchKeyword || ''}
          value={formik.values.hs_code_ai}
          options={
            isArray(dataHSCodeAIOptionsResp?.data?.data) && !dataHSCodeAIOptionsResp.isFetching
              ? dataHSCodeAIOptionsResp?.data?.data || []
              : []
          }
          onChange={(value: ExpectedAny) => {
            // setHSCodeValue(value)
            formik.setFieldValue('hs_code_ai', value);
          }}
          onTextInputChange={(value: string) => {
            setHsCodeAISearchKeyword(value);
          }}
          loading={dataHSCodeAIOptionsResp?.isFetching}
          isError={getError('hs_code_ai')}
        />
        <Box width={'100%'}>
          <Grid sx={{ padding: 0 }} container spacing={2}>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_from}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_from', data);
                  formik.setFieldTouched('trade_from');
                }}
                label={t('trade_data.search_form.input.trade_from')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                    helperText: renderFormItemError(getFormItemError('trade_from', formik)),
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_to}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_to', data);
                  formik.setFieldTouched('trade_to');
                }}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
          </Grid>
        </Box>
        {advancedSearchForm[searchBy]}
        {countTransactionHsCode}
      </Fragment>
    ),
    [TradeDataPublicSearchBy.COMPANY_BUYER]: (
      <Fragment>
        <TextField
          size="small"
          value={values.keyword}
          id="keyword"
          name="keyword"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          fullWidth
          placeholder="Typing..."
          label="Keyword (Company Name)"
        />
        <Box width={'100%'}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_from}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_from', data);
                  formik.setFieldTouched('trade_from');
                }}
                label={t('trade_data.search_form.input.trade_from')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                    helperText: renderFormItemError(getFormItemError('trade_from', formik)),
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_to}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_to', data);
                  formik.setFieldTouched('trade_to');
                }}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
          </Grid>
        </Box>
        {advancedSearchForm[searchBy]}
        {countTransactionCompany}
      </Fragment>
    ),
    [TradeDataPublicSearchBy.COMPANY_SUPPLIER]: (
      <Fragment>
        <TextField
          size="small"
          value={values.keyword}
          id="keyword"
          name="keyword"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          fullWidth
          placeholder="Typing..."
          label="Keyword (Company Name)"
        />
        <Box width={'100%'}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_from}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_from', data);
                  formik.setFieldTouched('trade_from');
                }}
                label={t('trade_data.search_form.input.trade_from')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                    helperText: renderFormItemError(getFormItemError('trade_from', formik)),
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_to}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_to', data);
                  formik.setFieldTouched('trade_to');
                }}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
          </Grid>
        </Box>
        {advancedSearchForm[searchBy]}
        {countTransactionCompany}
      </Fragment>
    ),
    [TypeCompanyTradeDataSearch.BOTH]: (
      <Fragment>
        <TextField
          size="small"
          value={values.keyword}
          id="keyword"
          name="keyword"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          fullWidth
          placeholder="Typing..."
          label="Keyword (Company Name)"
        />
        <Box width={'100%'}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_from}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_from', data);
                  formik.setFieldTouched('trade_from');
                }}
                label={t('trade_data.search_form.input.trade_from')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                    helperText: renderFormItemError(getFormItemError('trade_from', formik)),
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_to}
                minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                onChange={(data) => {
                  formik.setFieldValue('trade_to', data);
                  formik.setFieldTouched('trade_to');
                }}
                label={t('trade_data.search_form.input.trade_to')}
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_from,
                    size: 'small',
                    fullWidth: true,
                    // required: true,
                  },
                }}
                views={['month', 'year']}
              />
            </Grid>
          </Grid>
        </Box>
        {advancedSearchForm[searchBy]}
        {countTransactionCompany}
      </Fragment>
    ),
  };

  const buttonExplore = useMemo(() => {
    return (
      <ButtonWithLoading
        disabled={isEmptyTransaction}
        isLoading={some([transactionsCountByHSCodeResp.isFetching, transactionsCountByCompanyRespCompany.isFetching])}
        size="large"
        variant="contained"
        isRounded
        type="submit"
      >
        <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
          {isTransactionQueryPaid ? (
            <CheckedCredit sx={{ mb: 0.5 }} fontSize="medium" />
          ) : (
            <CreditCostIndicator pointData={pointsViewTradeData} />
          )}
          <Stack flexDirection={'row'} alignItems={'center'} gap={0.5}>
            <TravelExplore />
            Explore
          </Stack>
        </Stack>
      </ButtonWithLoading>
    );
  }, [transactionsCountByHSCodeResp, transactionsCountByCompanyRespCompany]);

  return (
    <WhiteBoxContent sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10)' }}>
      <FormikForm formik={formik}>
        <Stack
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: borderRadius * 999,
            },
          }}
          padding={spacing(6)}
          borderRadius={borderRadius}
          textAlign={'center'}
          justifyContent={'center'}
          alignItems="center"
          spacing={3}
        >
          <Box
            color={common.white}
            display={'flex'}
            alignItems="center"
            justifyContent={'center'}
            width={64}
            height={64}
            sx={{ background: background.gradientColor }}
            borderRadius={borderRadius}
          >
            <Search size={40} />
          </Box>

          <Box>
            <Typography sx={{ textWrap: 'balance' }} variant="h4">
              What trade data are you looking for?
            </Typography>
            <Typography sx={{ textWrap: 'balance' }} variant="subtitle1" color={'text.secondary'}>
              Smarter, Faster, Accurate. Search global trade data using HS codes or describe products in natural
              language
            </Typography>
          </Box>

          <Stack gap={1} justifyContent={'center'} flexWrap={'wrap'} direction={'row'}>
            {filterSearchBy}
          </Stack>

          <SectionErrorBoundary resetKey="SearchTradeData" sectionName="SearchTradeData">
            {searchForm[searchBy]}
          </SectionErrorBoundary>
          {buttonExplore}
        </Stack>
      </FormikForm>
    </WhiteBoxContent>
  );
};

export default TradeDataMainSearch;
